import React from "react";
import {
  TextField,
  Box,
  IconButton,
  Chip,
  Paper,
  Typography,
  Fade,
  CircularProgress,
  Tooltip,
  Collapse,
} from "@mui/material";
import {
  Send,
  AutoAwesome,
  SmartToy,
  AttachFile,
  Mic,
  Image,
} from "@mui/icons-material";

const AIPromptTextField = ({
  // Value and state
  value = "",
  onChange,
  onSend,

  // Variants and styling
  variant = "standard",
  size = "medium",
  fullWidth = true,

  // Features
  withSuggestions = true,
  withQuickActions = false,
  withCharacterCounter = false,
  multiline = true,
  maxRows = 4,

  // Content
  placeholder = "Ask AI anything...",
  suggestions = [
    "Write a creative story about...",
    "Explain quantum computing in simple terms",
    "Help me plan a workout routine",
    "Generate code for a React component",
  ],

  // Quick actions
  quickActions = [
    {
      icon: <Image />,
      label: "Generate image",
      action: () => console.log("Generate image"),
    },
    {
      icon: <AttachFile />,
      label: "Attach file",
      action: () => console.log("Attach file"),
    },
    {
      icon: <Mic />,
      label: "Voice input",
      action: () => console.log("Voice input"),
    },
  ],

  // Behavior
  autoFocus = false,
  disabled = false,
  loading = false,
  maxLength = 1000,

  // Customization
  borderRadius = 3,
  showSendButton = true,
  customSendIcon = <Send />,

  // Events
  onFocus,
  onBlur,
  onKeyPress,

  ...textFieldProps
}) => {
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  const handleSend = () => {
    if (value.trim() && onSend && !loading) {
      onSend(value.trim());
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }

    if (onKeyPress) {
      onKeyPress(e);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    setShowSuggestions(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    setTimeout(() => setShowSuggestions(false), 200);
    if (onBlur) onBlur(e);
  };

  const handleSuggestionClick = (suggestion) => {
    if (onChange) {
      onChange(suggestion);
    }
    setShowSuggestions(false);
  };

  const handleQuickAction = (action) => {
    if (action && typeof action.action === "function") {
      action.action();
    }
  };

  const characterCount = value.length;
  const isNearLimit = characterCount > maxLength * 0.9;

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto" }}>
      {/* Quick Actions */}
      {withQuickActions && quickActions.length > 0 && (
        <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
          {quickActions.map((action, index) => (
            <Tooltip key={index} title={action.label}>
              <IconButton
                size="small"
                onClick={() => handleQuickAction(action)}
                disabled={disabled}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1,
                }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* Suggestions */}
      {withSuggestions && suggestions.length > 0 && (
        <Collapse in={showSuggestions && !value && isFocused} unmountOnExit>
          <Fade in={showSuggestions && !value && isFocused} timeout={400}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                backgroundColor: "background.default",
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Try asking...
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {suggestions.map((suggestion, index) => (
                  <Chip
                    key={index}
                    label={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    variant="outlined"
                    size="small"
                    icon={<SmartToy />}
                    sx={{
                      borderRadius: 1.5,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "primary.light",
                        color: "white",
                      },
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Fade>
        </Collapse>
      )}

      {/* Main TextField */}
      <TextField
        value={value}
        onChange={(e) => onChange && onChange(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        placeholder={placeholder}
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        multiline={multiline}
        maxRows={maxRows}
        autoFocus={autoFocus}
        disabled={disabled || loading}
        inputProps={{ maxLength }}
        InputProps={{
          startAdornment: (
            <AutoAwesome
              sx={{
                mr: 1,
                color: "primary.main",
                opacity: 0.8,
              }}
            />
          ),
          endAdornment: showSendButton && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {withCharacterCounter && (
                <Typography
                  variant="caption"
                  color={isNearLimit ? "error" : "text.secondary"}
                  sx={{ mr: 0.5 }}
                >
                  {characterCount}/{maxLength}
                </Typography>
              )}

              {loading ? (
                <CircularProgress size={20} />
              ) : (
                <IconButton
                  onClick={handleSend}
                  disabled={!value.trim() || disabled}
                  size="small"
                  sx={{
                    color: value.trim() ? "primary.main" : "text.disabled",
                    transition: "all 0.2s",
                    "&:hover": {
                      backgroundColor: "primary.main",
                      color: "white",
                    },
                  }}
                >
                  {customSendIcon}
                </IconButton>
              )}
            </Box>
          ),
          sx: {
            borderRadius,
            backgroundColor:
              variant === "outlined" ? "background.paper" : "transparent",
            transition: "all 0.3s ease",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isFocused ? "primary.main" : "grey.300",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "primary.main",
            },
            ...(textFieldProps.InputProps?.sx || {}),
          },
        }}
        {...textFieldProps}
      />
    </Box>
  );
};

export default AIPromptTextField;
